/* eslint-disable jsdoc/check-param-names */
import type { KeycloackApi } from './keycloack-api.types'
import { REFRESH_TOKEN_STORAGE_KEY } from '~/constants'
import env from '~/env'
import storage from '~/foundation/storage/localstorage'
import http from './keycloack-api.http'
import {
  cleanUrlParams,
  getRedirectUri,
  getStoredQueryParams,
  redirect,
  storeQueryParams,
} from './keycloack-api.redirect'

export { cleanUrlParams, getRedirectUri, getStoredQueryParams, redirect, storeQueryParams }

/**
 * Retrieves the access token using the authorization code.
 *
 * @param code - The authorization code.
 * @returns The access token.
 */
export async function getAccessToken(code: string) {
  const redirectUri = getRedirectUri()
  const body = new URLSearchParams([
    ['grant_type', 'authorization_code'],
    ['code', code],
    ['redirect_uri', redirectUri],
    ['client_id', env.VITE_KEYCLOAK_CLIENT_ID],
  ])

  const res = await http.post<KeycloackApi.KeycloackTokenResponse>('token', body)
  return res.data
}

/**
 * Retrieves a new access token using the refresh token.
 *
 * @param refreshToken - The refresh token.
 * @returns The new access token.
 */
export async function refreshAccessToken() {
  const token = storage.get(REFRESH_TOKEN_STORAGE_KEY)

  const redirectUri = getRedirectUri()

  const body = new URLSearchParams()
  body.append('grant_type', 'refresh_token')
  body.append('redirect_uri', redirectUri)
  body.append('refresh_token', token as string)
  body.append('client_id', env.VITE_KEYCLOAK_CLIENT_ID)

  const res = await http.post('token', body)
  return res.data
}

/**
 * Retrieves the user information.
 *
 * @returns The user information.
 */
export async function getUserInfo() {
  const res = await http.get<KeycloackApi.GetUserInfoResponse>('/userinfo')
  return res.data
}

/**
 * Logout the user.
 */
export async function logout(refreshToken: string) {
  const redirectUri = getRedirectUri()
  const url = new URLSearchParams()
  url.append('redirect_uri', encodeURI(redirectUri))
  url.append('client_id', env.VITE_KEYCLOAK_CLIENT_ID)
  url.append('refresh_token', refreshToken)

  await http.post('/logout', url)
}

export default {
  cleanUrlParams,
  redirect,
  getAccessToken,
  getRedirectUri,
  getUserInfo,
  refreshAccessToken,
  storeQueryParams,
  logout,
  getStoredQueryParams,
}
